import React, { Children, useState } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { usePhotosContext } from 'Context/photos';

import { applyCypressData } from 'Utilities/index';
import type { PhotoCommonType, PhotoType } from 'Utilities/profiling/@types';
import { getSectionState } from 'Utilities/sectionState';

import { useVehicleProfilingData } from '../DataFlow/VehicleProfilingContext.helpers';
import type { PhotoInput } from '../Photos/ImageUpload/photosSections';
import { conditionSection } from '../Photos/ImageUpload/photosSections';
import { ProfilingInfoBox } from '../ProfilingInfoBox/ProfilingInfoBox';
import { CONFIRM_DETAILS_SECTIONS } from '../VehicleProfiling.constants';
import { isRetakeStatus } from '../VehicleProfiling.helpers';
import LocalTexts from '../VehicleProfilingTexts.json';

import { ConfirmButton } from './components/ConfirmButton/ConfirmButton';
import { SectionExpand } from './components/SectionExpand/SectionExpand';
import { SectionHead } from './components/SectionHead/SectionHead';
import { ConditionImage } from './ConditionImage';
import { DAMAGES_IN_WIZARD } from './ConfirmDetails.helpers';
import { useConditionCopy, useGetSectionComplete } from './ConfirmDetails.hooks';
import type { confirmBlockProps } from './ConfirmDetails.types';
import { PhotosErrorBlock } from './PhotosErrorBlock';

import styles from './BlockWithImages.module.scss';

export const CONDITION_GRID = Object.keys(conditionSection)
  .reduce<PhotoInput[]>((acc, x) => [...acc, ...conditionSection[x].inputs], []);

const LocalT = new MDText(LocalTexts);

const { CONDITION } = CONFIRM_DETAILS_SECTIONS;

export type TextType = PhotoInput[] | string;
type SortedGridType = PhotoInput & { text: TextType };

export const ConditionBlock = ({
  buttonProps, isReadOnly, onRetakePictureClick = () => {}, redirectToPhotos, ...props
}: confirmBlockProps) => {
  const {
    desc, isCompleted, isDependencyCompleted, title,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const { premiumOfferData } = useVehicleProfilingData();
  const { vehicleDetails } = premiumOfferData || {};
  const { isError: isPhotosError, isLoading: isPhotosLoading, photos } = usePhotosContext();

  const { renderText, renderValue } = useConditionCopy();

  const { state: sectionState } = getSectionState({
    completed: isCompleted,
    dependencyCompleted: isDependencyCompleted,
  });
  const { completed, isActive, isComplete } = useGetSectionComplete(isReadOnly, sectionState);

  const conditionObj = photos?.filter((x) => !isRetakeStatus((x as PhotoType).status))
    ?.reduce<Record<string, PhotoCommonType[]>>((acc, x: PhotoCommonType) => {
      acc[x.kind] = acc[x.kind] || [];
      acc[x.kind].push(x);

      return acc;
    }, {});

  const sortedGrid = CONDITION_GRID.reduce<{
    images: SortedGridType[];
    texts: SortedGridType[];
  }>((acc, x: PhotoInput) => {
    const keyForDetails = DAMAGES_IN_WIZARD[x.name as keyof typeof DAMAGES_IN_WIZARD] as keyof typeof vehicleDetails;
    const vector = (conditionObj[x.name] && vehicleDetails?.[keyForDetails as keyof typeof vehicleDetails]) ? 'images' : 'texts';

    acc[vector].push({ ...x, text: renderText({ conditionObj, keyForDetails, x }) });

    return acc;
  }, { images: [], texts: [] });

  const { images, texts } = sortedGrid;

  return (
    <div className={cx(styles.component, styles[isReadOnly ? 'completed' : sectionState], { [styles.isOpenBlock]: isOpen })} id={CONDITION}>
      <SectionHead
        desc={desc}
        isOpen={isOpen}
        isReadOnly={isReadOnly}
        state={sectionState}
        title={title}
        toggleIsOpen={() => setIsOpen(!isOpen)}
      />
      <SectionExpand
        isOpen={isActive || isOpen}
      >
        <div className={styles.condition}>
          {isPhotosError ? <PhotosErrorBlock /> : (
            <>
              <div>
                {Children.toArray(images.map(({ label, name }) => conditionObj[name]
                  .map((photo, i) => {
                    const { damageMeta, presets, url }: Partial<PhotoType> = photo;

                    return (
                      <ConditionImage key={`${label}-${name}-${url}-${i}`} {...{
                        damageMeta, isPhotosLoading, label, name, presets, url,
                      }}
                      />
                    );
                  })))}

                {Children.toArray(texts.map(({ label, name, text }) => (
                  <div key={name} className={cx(styles.copy, { [styles.isOpen]: completed })}>
                    <span>
                      {label}
                    </span>
                    <div className={styles.text} data-testid={name} {...applyCypressData(name)}>
                      {renderValue(text)}
                    </div>
                  </div>
                )))}
              </div>
              {!isReadOnly && (
                <div className={cx(styles.footer, { [styles.isCompleted]: isComplete })}>
                  <div className={styles.hint}>
                    <ProfilingInfoBox showConstantly>
                      <div>
                        {LocalT.translate(
                          images?.length ? 'confirmDetails.sections.conditionDetails.hintWithDamage' : 'confirmDetails.sections.conditionDetails.hintWithoutDamage',
                          {
                            link: (
                              <Link onClick={(e) => onRetakePictureClick(e)} to={redirectToPhotos}>
                                {LocalT.translate('confirmDetails.sections.conditionDetails.link')}
                              </Link>
                            ),
                          },
                        )}
                      </div>
                    </ProfilingInfoBox>
                  </div>
                </div>
              )}
              {!completed && buttonProps && (
                <div className={styles.buttonContainer} >
                  <ConfirmButton {...buttonProps} />
                </div>
              )}
            </>
          )}
        </div>
      </SectionExpand>
    </div>
  );
};
